/**
 * ToastHost — renders the toast stack for the imperative `toast()` API.
 * Mounted once at the app root, next to ConfirmHost.
 */

import React, { useEffect, useRef, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, X } from 'lucide-react';
import { _registerToastHost } from './toast';
import './Toast.css';

const ICONS = { success: CheckCircle2, error: AlertCircle, info: Info };

export default function ToastHost() {
  const [toasts, setToasts] = useState([]);
  const nextId = useRef(1);

  useEffect(() => _registerToastHost((opts) => {
    const id = nextId.current++;
    setToasts((prev) => [...prev.slice(-3), { id, ...opts }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, opts.duration);
  }), []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-stack" role="status" aria-live="polite">
      {toasts.map((t) => {
        const Icon = ICONS[t.kind] || Info;
        return (
          <div key={t.id} className={`toast-item toast-item--${t.kind}`}>
            <Icon size={16} aria-hidden="true" />
            <span className="toast-message">{t.message}</span>
            {t.action && typeof t.action.run === 'function' && (
              <button
                type="button"
                className="toast-action"
                onClick={() => {
                  setToasts((prev) => prev.filter((x) => x.id !== t.id));
                  t.action.run();
                }}
              >
                {t.action.label || 'Undo'}
              </button>
            )}
            <button
              className="toast-dismiss"
              aria-label="Dismiss"
              onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
